export type DashboardView =
  | 'events'
  | 'reservations'
  | 'users'
  | 'profile'
  | 'messages'
  | 'menu'
  | 'reports'
  | 'tables';

export type MenuCategory = 'food' | 'drink';

export interface MenuItem {
  id: string;
  name: string;
  description?: string;
  category: MenuCategory;
  price: number | string;
  priceGlass?: number | string;
  priceBottle?: number | string;
  menuSection?: string;
  origin?: string;
  servingSizeMl?: number;
  imageUrl?: string;
  isAvailable: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface MenuItemPayload {
  name: string;
  description: string;
  category: MenuCategory;
  price: number;
  priceGlass?: number;
  priceBottle?: number;
  menuSection?: string;
  origin?: string;
  servingSizeMl?: number;
  imageUrl: string;
  isAvailable: boolean;
  sortOrder: number;
}

export type UserRole = 'admin' | 'manager' | 'user';

export interface PaginationResult<T> {
  items: T[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export enum ReservationStatus {
  PENDING_PAYMENT = 'pending_payment',
  BOOKED = 'booked',
  CONFIRMED = 'confirmed',
  CHECKED_IN = 'checked_in',
  CANCELLED = 'cancelled',
  PAID = 'paid',
  COMPLETED = 'completed',
}

export interface Reservation {
  eventId?: string;
  event?: {id: string; title: string};
  eventStartsAt?: string;
  eventEndsAt?: string;
  depositAmount?: number | string;
  depositCurrency?: string;
  paymentStatus?: string;
  id: string;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  date: string;
  timeStart: string;
  timeEnd?: string;
  table: any;
  additionalTables?: Table[];
  additionalTableIds?: string[];
  isVip: boolean;
  numberOfGuests: number;
  notes?: string;
  status: ReservationStatus;
  paidAmount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ReservationSummary {
  trendLast7Days: {
    date: string;
    totalCount: number;
    cancelledCount: number;
  }[];
  thisWeekCount: number;
  todayCount: number;
  nextHourCount: number;
  cancelRate: number;
  availableRegularTables?: number;
  availableVipTables?: number;
  totalRegularTables?: number;
  totalVipTables?: number;
  paidReservations?: number;
}

export interface CustomerReportRow {
  name: string;
  email: string;
  phone?: string;
  totalReservations: number;
  totalGuests: number;
  completedReservations: number;
  cancelledReservations: number;
  totalPaid: number;
  firstReservationAt: string;
  lastReservationAt: string;
  isRecurring: boolean;
}

export interface CustomerReport {
  startDate: string;
  endDate: string;
  totalCustomers: number;
  recurringCustomers: number;
  totalReservations: number;
  customers: CustomerReportRow[];
}

export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  createdAt: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  isActive: boolean;
  isDeleted: boolean;
  lastLogin?: string;
  lastLoginIp?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserPayload {
  name: string;
  email: string;
  password: string;
  role: UserRole;
}

export interface LoginResponse {
  accessToken: string;
}

export interface ResetPasswordPayload {
  token: string;
  newPassword: string;
}

export interface UpdateMyPasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export interface AuthPayload {
  role?: UserRole;
  email?: string;
}
export interface Table {
  id: string;
  tableNumber: string;
  shape: 'round' | 'rectangular';
  capacity: number;
  description?: string;
  isVip: boolean;
  reserved: boolean;
  position?: { x: number; y: number };
  tableImage?: any
  sectionId: string;
  section?: RestaurantSection;
};
export interface RestaurantSection {
  id: string;
  name: string;
  imageUrl?: string | null;
  imageUrls?: string[];
  description?: string | null;
  tables?: Table[];
}
export interface TablesTabProps {
  tables: Table[];
  apiBase: string;
  token: string;
  onAddTable: (form: any) => Promise<boolean>;
  onEditTable: (id: string, form: any) => Promise<boolean>;
  onDeleteTable: (id: string) => void;
}
